import type React from "react";
import { useState } from "react";
import {
  Box,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Typography,
  Paper,
  Card,
  CardContent,
  Checkbox,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import type translations from "../../translations/translations.json";
import type { Truck } from "../../types/truck";
import type { City } from "../../types/city";
import { EditorUsersSelect } from "../EditorUsersSelect";

type TranslationKeys = keyof typeof translations.en;

interface CreateTruckGroupFormProps {
  onSubmit: (groupData: {
    name: string;
    truckIds: string[];
    editorUserIds: string[];
  }) => Promise<void>;
  error: string | null;
  initialData?: {
    name: string;
    truckIds: string[];
    editorUserIds: string[];
  };
  onCancel?: () => void;
  cities: City[];
  trucks: Truck[];
  loadingTrucks?: boolean;
}

const CreateTruckGroupForm: React.FC<CreateTruckGroupFormProps> = ({
  onSubmit,
  error,
  initialData,
  onCancel,
  cities,
  trucks,
  loadingTrucks = false,
}) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: initialData?.name || "",
    truckIds: initialData?.truckIds || [],
    editorUserIds: initialData?.editorUserIds || [],
  });
  const [searchQuery, setSearchQuery] = useState("");
  const { t } = useTranslation();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.truckIds.length === 0) {
      return;
    }
    setLoading(true);
    try {
      await onSubmit(formData);
      setFormData({ name: "", truckIds: [], editorUserIds: [] });
    } finally {
      setLoading(false);
    }
  };

  const handleToggleTruck = (truckId: string) => {
    setFormData((prev) => {
      const newTruckIds = prev.truckIds.includes(truckId)
        ? prev.truckIds.filter((id) => id !== truckId)
        : [...prev.truckIds, truckId];
      return { ...prev, truckIds: newTruckIds };
    });
  };

  const getCityName = (cityId: string) => {
    const city = cities.find((c) => c._id === cityId);
    return city ? city.name : cityId;
  };

  const filteredTrucks = trucks.filter((truck) =>
    truck.licensePlate.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ maxWidth: 1200, mx: "auto" }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          {t("Name" as TranslationKeys)}
        </Typography>
        <TextField
          autoFocus
          label={t("Name" as TranslationKeys)}
          type="text"
          fullWidth
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          required
        />
      </Paper>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          {t("Editors" as TranslationKeys)}
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          {t("Select users who can edit this truck group" as TranslationKeys)}
        </Typography>
        <Box sx={{ mt: 2 }}>
          <EditorUsersSelect
            value={formData.editorUserIds}
            onChange={(editorUserIds) => setFormData({ ...formData, editorUserIds })}
          />
        </Box>
      </Paper>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ mb: 2 }}>
          <Typography variant="h6" gutterBottom>
            {t("Select Trucks" as TranslationKeys)}
          </Typography>
          <TextField
            label={t("Search Trucks" as TranslationKeys)}
            type="text"
            fullWidth
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ mb: 2 }}
          />
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {t("Selected Trucks: {{count}}" as TranslationKeys, {
              count: formData.truckIds.length,
            })}
          </Typography>
        </Box>
        {loadingTrucks ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
              gap: 1,
            }}
          >
            {filteredTrucks.map((truck) => {
              const currentState = truck.currentState || {};
              const location = currentState.location || {};
              const status = currentState.status || "UNKNOWN";

              return (
                <Card
                  key={truck._id}
                  sx={{
                    border: formData.truckIds.includes(truck._id)
                      ? "2px solid primary.main"
                      : "1px solid divider",
                    cursor: "pointer",
                    "&:hover": {
                      backgroundColor: "action.hover",
                    },
                  }}
                  onClick={() => handleToggleTruck(truck._id)}
                >
                  <CardContent sx={{ p: 1 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        mb: 0.5,
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Typography variant="subtitle2" component="div" noWrap>
                          {truck.licensePlate}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ opacity: 0.7 }}
                          noWrap
                        >
                          {truck.type}
                        </Typography>
                      </Box>
                      <Box onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          size="small"
                          checked={formData.truckIds.includes(truck._id)}
                          onChange={() => handleToggleTruck(truck._id)}
                        />
                      </Box>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 0.5,
                      }}
                    >
                      <Typography variant="caption" color="text.secondary" noWrap>
                        {t("Location" as TranslationKeys)}:{" "}
                        {location.cityId ? getCityName(location.cityId) : "-"}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" noWrap>
                        {t("Status" as TranslationKeys)}: {status}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              );
            })}
          </Box>
        )}
      </Paper>

      <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
        {onCancel && (
          <Button onClick={onCancel} variant="outlined">
            {t("Cancel" as TranslationKeys)}
          </Button>
        )}
        <Button
          type="submit"
          variant="contained"
          disabled={loading || formData.truckIds.length === 0}
        >
          {loading ? (
            <CircularProgress size={24} />
          ) : initialData ? (
            t("Save changes" as TranslationKeys)
          ) : (
            t("Create Group" as TranslationKeys)
          )}
        </Button>
      </Box>
    </Box>
  );
};

export default CreateTruckGroupForm;
